import { getTimeout, getWindow } from '@/helpers/globals.ts'
import { useScriptTag } from '@vueuse/core'
import { useGtmVar } from '@/composables/app/gtm/useGtmVar.ts'
import { type App, inject, watch } from 'vue'

const key = 'plugin.ms.clarity.id'

export function provideMsClarityId(app: App, url: string | undefined): void {
    app.provide(key, url)
}

export function useMsClarityId(): string | undefined {
    return inject(key) as string
}

export function useMsClarity(): void {
    const window = getWindow()
    const document = window?.document
    const id = useMsClarityId()

    if (!document || !id) {
        return
    }

    window.clarity = createClarity()

    const { load } = useScriptTag(
        `https://www.clarity.ms/tag/${id}`,
        undefined,
        {
            async: true,
            immediate: false,
        }
    )

    const timeout = getTimeout()
    if (timeout) {
        const { setTimeout } = timeout

        setTimeout(async () => {
            await load()
        }, 10_000)
    }

    const customerId = useGtmVar<string>('customer-id')
    const sessionId = useGtmVar<string>('session-id')
    const amazonTraceId = useGtmVar<string>('amazon-trace-id')

    const setClarityVar = (key: string, value: string | undefined) => {
        if (!value) {
            return
        }

        window?.clarity?.('set', key, value)
    }

    watch(
        customerId,
        (newCustomerId) => {
            setClarityVar('customer-id', newCustomerId)
        },
        { immediate: true }
    )

    watch(
        sessionId,
        (newSessionId) => {
            setClarityVar('session-id', newSessionId)
        },
        { immediate: true }
    )

    watch(
        amazonTraceId,
        (newAmazonTraceId) => {
            setClarityVar('amazon-trace-id', newAmazonTraceId)
        },
        { immediate: true }
    )
}

function createClarity(): Clarity {
    const clarity: Clarity = function (...args: any) {
        clarity.q.push(args)
    }

    clarity.q = []

    return clarity
}

export type Clarity = {
    q: any[]
    (...args: any): void
}
